import { useState, useMemo, useEffect } from "react";
import {
  Tabs,
  Table,
  Card,
  Text,
  Group,
  Button,
  Badge,
  Alert,
  Modal,
  TextInput,
  Textarea,
  PasswordInput,
  Switch,
  Stack,
  ActionIcon,
  Select,
  MultiSelect,
  Loader,
  Center,
  Divider,
  CopyButton,
  Tooltip,
  ScrollArea,
  SegmentedControl,
  Anchor,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconUsers,
  IconUser,
  IconAppWindow,
  IconTrash,
  IconLock,
  IconPlayerPause,
  IconPlayerPlay,
  IconSettings,
  IconPlus,
  IconSearch,
  IconCopy,
  IconCheck,
  IconX,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../components/PageHeader";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { PageTransition } from "../../components/PageTransition";
import { showSuccess, showError } from "../../lib/notifications";
import { keycloakApi, servicesApi, KcUser, KcRole, KcGroup } from "../../lib/api";

interface KcClient {
  id: string;
  clientId: string;
  protocol: string;
  enabled: boolean;
  redirectUris: string[];
  baseUrl: string;
}


// Collapse { role, source } rows into one entry per role, listing its sources.
function groupRoles<T extends { role: string; source: string }>(rows: T[]) {
  const roles = new Map<string, string[]>();
  for (const r of rows) roles.set(r.role, [...(roles.get(r.role) || []), r.source]);
  return Array.from(roles, ([role, sources]) => ({ role, sources }));
}

// A client's roles as chips: blue = assigned directly (removable), gray =
// inherited from a group (hover for which). The `*-access` gate is hidden when
// another role is present, since level roles already include it.
function RoleChips({
  roles,
  onRemove,
  removing,
}: {
  roles: { role: string; sources: string[] }[];
  onRemove?: (role: string) => void;
  removing?: string | null;
}) {
  const visible =
    roles.length > 1 ? roles.filter((r) => !r.role.endsWith("-access")) : roles;
  return (
    <Group spacing={6}>
      {visible.map(({ role, sources }) => {
        const direct = sources.includes("direct");
        const groups = sources.filter((s) => s !== "direct");
        const label = [direct && "Assigned directly", ...groups.map((g) => `From group ${g}`)]
          .filter(Boolean)
          .join(" · ");
        return (
          <Tooltip key={role} label={label} withArrow>
            <Badge
              color={direct ? "blue" : "gray"}
              variant="light"
              size="lg"
              radius="sm"
              sx={{ textTransform: "none", fontWeight: 500 }}
              rightSection={
                direct && onRemove ? (
                  <ActionIcon
                    size="xs"
                    radius="xl"
                    variant="transparent"
                    loading={removing === role}
                    onClick={() => onRemove(role)}
                    aria-label={`Remove ${role}`}
                  >
                    <IconX size={12} />
                  </ActionIcon>
                ) : undefined
              }
            >
              {role}
            </Badge>
          </Tooltip>
        );
      })}
    </Group>
  );
}

// Search users by email (2+ characters) and pick one.
function UserSearchSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 300);
  const { data: found } = useQuery({
    queryKey: ["kc-users", debounced],
    queryFn: () => keycloakApi.listUsers(debounced).then((r) => r.data),
    enabled: debounced.trim().length >= 2,
  });
  // Clear the search box once the parent resets the pick.
  useEffect(() => {
    if (!value) setSearch("");
  }, [value]);

  return (
    <Select
      label="User"
      placeholder="Search by email"
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      filter={() => true}
      nothingFound={debounced.trim().length >= 2 ? "No users found" : "Type 2+ characters"}
      data={(found || []).map((u) => ({
        value: u.id,
        label: u.email || u.username,
      }))}
      value={value}
      onChange={onChange}
    />
  );
}

// Every group as a picker option, labelled by path (/acme/admins).
function useGroupOptions() {
  const { data: tree } = useQuery({
    queryKey: ["kc-group-tree"],
    queryFn: () => keycloakApi.groupTree().then((r) => r.data),
  });
  return useMemo(
    () =>
      (tree || [])
        .flatMap((g) => [g, ...g.subGroups])
        .map((g) => ({ value: g.id, label: g.path }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [tree]
  );
}

// Everything that shows who holds which role, after a group or membership change.
function invalidateRoleViews(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [
    "kc-group-tree",
    "kc-group-roles",
    "kc-group-members",
    "kc-user-groups",
    "kc-user-client-roles",
    "kc-client-members",
  ]) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

// Read-only field with an inline copy button — for SAML config values.
function CopyField({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description?: string;
}) {
  return (
    <TextInput
      label={label}
      description={description}
      value={value}
      readOnly
      rightSection={
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
              <ActionIcon color={copied ? "teal" : "gray"} onClick={copy}>
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}

// ===========================================================================
// Users tab
// ===========================================================================

function UsersTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<KcUser | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ["kc-users", search],
    queryFn: () => keycloakApi.listUsers(search || undefined).then((r) => r.data),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["kc-users"] });
  const groupOptions = useGroupOptions();

  const createForm = useForm({
    initialValues: {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      temporaryPassword: true,
      sendInvite: true,
      groupIds: [] as string[],
    },
    validate: {
      username: (v) => (v.trim().length < 1 ? "Username is required" : null),
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: typeof createForm.values) =>
      keycloakApi.createUser({
        username: values.username.trim(),
        email: values.email || undefined,
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        password: values.password || undefined,
        temporaryPassword: values.temporaryPassword,
        sendInvite: values.sendInvite,
        groupIds: values.groupIds,
      }),
    onSuccess: ({ data }) => {
      if (data.failedGroups?.length) {
        showError(
          `User created, but adding them to ${data.failedGroups.length} group(s) failed. Add them from the user's Permissions tab.`
        );
      } else if (data.inviteError) {
        showError(`User created, but the invite email failed: ${data.inviteError}`);
      } else {
        showSuccess(data.inviteSent ? "User created and invite sent" : "User created");
      }
      setCreateOpen(false);
      createForm.reset();
      invalidate();
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack>
      <Group position="apart">
        <TextInput
          placeholder="Search users..."
          icon={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={(theme) => ({
            width: 300,
            [theme.fn.smallerThan('sm')]: { width: '100%' },
          })}
        />
        <Button leftIcon={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
          New user
        </Button>
      </Group>

      <Card shadow="sm" radius="md" withBorder>
      <ScrollArea>
      <Table highlightOnHover>
        <thead>
          <tr>
            <th>Username</th>
            <th>Email</th>
            <th>Name</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {(users || []).map((u) => (
            <tr
              key={u.id}
              style={{ cursor: "pointer" }}
              onClick={() => setDetailUser(u)}
            >
              <td>
                <Text weight={500}>{u.username}</Text>
              </td>
              <td>{u.email || "—"}</td>
              <td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</td>
              <td>
                <Badge color={u.enabled ? "green" : "gray"}>
                  {u.enabled ? "Enabled" : "Disabled"}
                </Badge>
              </td>
            </tr>
          ))}
          {(users || []).length === 0 && (
            <tr>
              <td colSpan={4}>
                <Text color="dimmed" align="center" py="md">
                  No users found.
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </ScrollArea>
      </Card>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Keycloak user"
      >
        <form onSubmit={createForm.onSubmit((v) => createMutation.mutate(v))}>
          <Stack>
            <TextInput
              label="Username"
              required
              {...createForm.getInputProps("username")}
            />
            <TextInput label="Email" {...createForm.getInputProps("email")} />
            <Group grow>
              <TextInput label="First name" {...createForm.getInputProps("firstName")} />
              <TextInput label="Last name" {...createForm.getInputProps("lastName")} />
            </Group>
            <PasswordInput
              label="Initial password"
              description="Optional. Leave blank to set later."
              {...createForm.getInputProps("password")}
            />
            <Switch
              label="Temporary password (require reset on first login)"
              checked={createForm.values.temporaryPassword}
              onChange={(e) =>
                createForm.setFieldValue("temporaryPassword", e.currentTarget.checked)
              }
            />
            <Switch
              label="Send invite email"
              description="Emails a link to confirm their address, valid for 7 days. Needs an email."
              checked={createForm.values.sendInvite}
              onChange={(e) => createForm.setFieldValue("sendInvite", e.currentTarget.checked)}
            />
            <MultiSelect
              label="Groups"
              description="Their roles come from these groups, e.g. /acme/developers."
              placeholder="Pick groups"
              searchable
              data={groupOptions}
              {...createForm.getInputProps("groupIds")}
            />
            <Group position="right">
              <Button variant="default" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createMutation.isLoading}>
                Create
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {detailUser && (
        <UserDetailModal
          user={detailUser}
          onClose={() => setDetailUser(null)}
          onChanged={invalidate}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Tabbed user modal: Details / Permissions / Password
// ---------------------------------------------------------------------------

function UserDetailModal({
  user,
  onClose,
  onChanged,
}: {
  user: KcUser;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<string | null>("details");
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || "—";

  return (
    <Modal
      opened
      onClose={onClose}
      title={`User — ${user.username}`}
      size={"xl"}
      styles={{ body: { minHeight: 300 } }}
    >
      <Tabs value={tab} onTabChange={setTab} keepMounted={false}>
        <Tabs.List sx={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
          <Tabs.Tab value="details" icon={<IconUser size={16} />}>
            Details
          </Tabs.Tab>
          <Tabs.Tab value="permissions" icon={<IconSettings size={16} />}>
            Permissions
          </Tabs.Tab>
          <Tabs.Tab value="password" icon={<IconLock size={16} />}>
            Password
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" pt="md">
          <UserDetailsPanel user={user} fullName={fullName} onChanged={onChanged} onClose={onClose} />
        </Tabs.Panel>

        <Tabs.Panel value="permissions" pt="md">
          <UserPermissionsPanel user={user} />
        </Tabs.Panel>

        <Tabs.Panel value="password" pt="md">
          <ResetPasswordPanel user={user} onClose={onClose} />
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}

function UserDetailsPanel({
  user,
  fullName,
  onChanged,
  onClose,
}: {
  user: KcUser;
  fullName: string;
  onChanged: () => void;
  onClose: () => void;
}) {
  // Track enabled locally so the badge reflects toggles without a refetch.
  const [enabled, setEnabled] = useState(user.enabled);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const toggleMutation = useMutation({
    mutationFn: () =>
      enabled ? keycloakApi.disableUser(user.id) : keycloakApi.enableUser(user.id),
    onSuccess: () => {
      setEnabled((v) => !v);
      showSuccess(enabled ? "User disabled" : "User enabled");
      onChanged();
    },
    onError: (e) => showError(e),
  });

  const deleteMutation = useMutation({
    mutationFn: () => keycloakApi.deleteUser(user.id),
    onSuccess: () => {
      showSuccess("User deleted");
      setConfirmDelete(false);
      onChanged();
      onClose();
    },
    onError: (e) => showError(e),
  });

  const inviteMutation = useMutation({
    mutationFn: () => keycloakApi.sendInvite(user.id),
    onSuccess: () => showSuccess(`Invite sent to ${user.email}`),
    onError: (e) => showError(e),
  });

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Group position="apart" align="center">
      <Text size="sm" color="dimmed">
        {label}
      </Text>
      <div style={{ textAlign: "right" }}>{value}</div>
    </Group>
  );

  return (
    <Stack>
      <Row label="Username" value={<Text weight={500}>{user.username}</Text>} />
      <Divider />
      <Row label="Email" value={<Text>{user.email || "—"}</Text>} />
      <Divider />
      <Row label="Name" value={<Text>{fullName}</Text>} />
      <Divider />
      <Row
        label="Status"
        value={
          <Badge color={enabled ? "green" : "gray"}>
            {enabled ? "Enabled" : "Disabled"}
          </Badge>
        }
      />

      <Group position="apart" mt="md">
        <Button
          variant="default"
          leftIcon={enabled ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
          loading={toggleMutation.isLoading}
          onClick={() => toggleMutation.mutate()}
        >
          {enabled ? "Disable user" : "Enable user"}
        </Button>
        {user.email && (
          <Button
            variant="default"
            loading={inviteMutation.isLoading}
            onClick={() => inviteMutation.mutate()}
          >
            {user.emailVerified ? "Send invite again" : "Send invite"}
          </Button>
        )}
        <Button
          color="red"
          variant="light"
          leftIcon={<IconTrash size={16} />}
          onClick={() => setConfirmDelete(true)}
        >
          Delete
        </Button>
      </Group>

      <ConfirmationModal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete user"
        message={`Permanently delete "${user.username}" from Keycloak? This cannot be undone.`}
        confirmLabel="Delete"
      />
    </Stack>
  );
}

// The user's groups: join one to get its roles, ✕ to leave.
function UserGroupsSection({ user }: { user: KcUser }) {
  const qc = useQueryClient();
  const options = useGroupOptions();
  const { data: groups, isLoading } = useQuery({
    queryKey: ["kc-user-groups", user.id],
    queryFn: () => keycloakApi.getUserGroups(user.id).then((r) => r.data),
  });
  const [groupId, setGroupId] = useState<string | null>(null);

  const join = useMutation({
    mutationFn: () => keycloakApi.addUserToGroup(user.id, groupId!),
    onSuccess: () => {
      showSuccess("Added to group");
      setGroupId(null);
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  const leave = useMutation({
    mutationFn: (id: string) => keycloakApi.removeUserFromGroup(user.id, id),
    onSuccess: () => {
      showSuccess("Removed from group");
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  if (isLoading) return <Loader size="sm" />;

  return (
    <Stack spacing="xs">
      <Text size="sm" weight={500}>
        Groups
      </Text>
      <Group spacing={6}>
        {(groups || []).map((g) => (
          <Badge
            key={g.id}
            variant="light"
            size="lg"
            radius="sm"
            sx={{ textTransform: "none", fontWeight: 500 }}
            rightSection={
              <ActionIcon
                size="xs"
                radius="xl"
                variant="transparent"
                loading={leave.isLoading && leave.variables === g.id}
                onClick={() => leave.mutate(g.id)}
                aria-label={`Remove from ${g.path}`}
              >
                <IconX size={12} />
              </ActionIcon>
            }
          >
            {g.path}
          </Badge>
        ))}
        {!groups?.length && (
          <Text size="sm" color="dimmed">
            Not in any group.
          </Text>
        )}
      </Group>
      <Group align="flex-end">
        <Select
          placeholder="Add to group"
          searchable
          data={options.filter((o) => !(groups || []).some((g) => g.id === o.value))}
          value={groupId}
          onChange={setGroupId}
          style={{ flex: 1 }}
        />
        <Button disabled={!groupId} loading={join.isLoading} onClick={() => join.mutate()}>
          Add
        </Button>
      </Group>
    </Stack>
  );
}

// The user's groups, and every client role they hold, direct or via a group.
function UserPermissionsPanel({ user }: { user: KcUser }) {
  const { data: mappings, isLoading } = useQuery({
    queryKey: ["kc-user-client-roles", user.id, "all"],
    queryFn: () => keycloakApi.listUserClientRoleMappings(user.id).then((r) => r.data),
  });

  const rows = useMemo(() => {
    const byClient = new Map<string, typeof mappings>();
    for (const m of mappings || []) byClient.set(m.clientId, [...(byClient.get(m.clientId) || []), m]);
    return Array.from(byClient, ([clientId, list]) => ({ clientId, roles: groupRoles(list || []) }));
  }, [mappings]);

  if (isLoading) return <Loader size="sm" />;

  return (
    <Stack>
      <UserGroupsSection user={user} />
      <Divider />
      {!rows.length ? (
        <Text size="sm" color="dimmed">
          No client roles. This user can't sign in to any restricted app.
        </Text>
      ) : (
        <Table fontSize="sm" verticalSpacing="sm">
          <thead>
            <tr>
              <th>Client</th>
              <th>Roles</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ clientId, roles }) => (
              <tr key={clientId}>
                <td style={{ wordBreak: "break-all" }}>{clientId}</td>
                <td>
                  <RoleChips roles={roles} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Text size="xs" color="dimmed">
        Blue: assigned directly. Gray: from a group (hover to see which). Change a group's
        roles under <b>Groups</b>; grant a one-off role from the app's <b>Users</b> tab under{" "}
        <b>Clients</b>.
      </Text>
    </Stack>
  );
}

// Users holding a role on a client: grant roles, remove direct ones.
function ClientUsersPanel({ client }: { client: KcClient }) {
  const qc = useQueryClient();
  const membersKey = ["kc-client-members", client.id];
  const { data: members, isLoading } = useQuery({
    queryKey: membersKey,
    queryFn: () => keycloakApi.listClientMembers(client.id).then((r) => r.data),
  });
  const { data: clientRoles } = useQuery({
    queryKey: ["kc-client-roles", client.id],
    queryFn: () => keycloakApi.listClientRoles(client.id).then((r) => r.data),
  });
  // `restricted-access` is internal: the gate role grants it.
  const assignable = (clientRoles || []).filter((r) => r.name !== "restricted-access");

  const rows = useMemo(() => {
    const byUser = new Map<string, typeof members>();
    for (const m of members || []) byUser.set(m.userId, [...(byUser.get(m.userId) || []), m]);
    return Array.from(byUser, ([userId, list]) => ({
      userId,
      // Email identifies people; username only when there's no email.
      label: list![0].email || list![0].username,
      roles: groupRoles(list || []),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [members]);

  const [userId, setUserId] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: membersKey });
    qc.invalidateQueries({ queryKey: ["kc-user-client-roles"] });
  };

  const add = useMutation({
    mutationFn: () =>
      keycloakApi.addUserClientRoles(
        userId!,
        client.id,
        assignable.filter((r) => picked.includes(r.name))
      ),
    onSuccess: () => {
      showSuccess("Roles added");
      setUserId(null);
      setPicked([]);
      refresh();
    },
    onError: (e) => showError(e),
  });

  const remove = useMutation({
    mutationFn: (m: { userId: string; role: string }) =>
      keycloakApi.removeUserClientRoles(
        m.userId,
        client.id,
        assignable.filter((r) => r.name === m.role)
      ),
    onSuccess: () => {
      showSuccess("Role removed");
      refresh();
    },
    onError: (e) => showError(e),
  });

  return (
    <Stack>
      <Group align="flex-end" grow>
        <UserSearchSelect value={userId} onChange={setUserId} />
        <MultiSelect
          label="Roles"
          placeholder="Pick roles"
          data={assignable.map((r) => ({ value: r.name, label: r.name }))}
          value={picked}
          onChange={setPicked}
        />
      </Group>
      <Group position="right">
        <Button
          leftIcon={<IconPlus size={16} />}
          disabled={!userId || !picked.length}
          loading={add.isLoading}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </Group>
      <Divider />
      {isLoading ? (
        <Loader size="sm" />
      ) : !rows.length ? (
        <Text size="sm" color="dimmed">
          Nobody has a role on this client yet.
        </Text>
      ) : (
        <Table fontSize="sm" verticalSpacing="sm">
          <thead>
            <tr>
              <th>User</th>
              <th>Roles</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.userId}>
                <td>{u.label}</td>
                <td>
                  <RoleChips
                    roles={u.roles}
                    onRemove={(role) => remove.mutate({ userId: u.userId, role })}
                    removing={
                      remove.isLoading && remove.variables?.userId === u.userId
                        ? remove.variables.role
                        : null
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Text size="xs" color="dimmed">
        Blue: assigned directly (✕ removes). Gray: from a group, changed on the group. Changes
        apply on the user's next login. Signing in needs the{" "}
        <code>{client.clientId}-access</code> gate, directly or through a role that includes it
        (the level roles do).
      </Text>
    </Stack>
  );
}

function ResetPasswordPanel({ user, onClose }: { user: KcUser; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [temporary, setTemporary] = useState(true);
  const mutation = useMutation({
    mutationFn: () => keycloakApi.resetPassword(user.id, password, temporary),
    onSuccess: () => {
      showSuccess("Password reset");
      onClose();
    },
    onError: (e) => showError(e),
  });

  return (
    <Stack>
      <PasswordInput
        label="New password"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
      />
      <Switch
        label="Temporary (require change on next login)"
        checked={temporary}
        onChange={(e) => setTemporary(e.currentTarget.checked)}
      />
      <Group position="right">
        <Button
          disabled={!password}
          loading={mutation.isLoading}
          onClick={() => mutation.mutate()}
        >
          Reset password
        </Button>
      </Group>
    </Stack>
  );
}

// ===========================================================================
// Clients tab
// ===========================================================================

// Shown once after an OIDC create: the secret, Copy, and "Store in service env".
function ClientSecretPanel({
  created,
  onClose,
}: {
  created: { id: string; clientId: string; secret: string };
  onClose: () => void;
}) {
  const { data: services } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list().then((r) => r.data),
  });
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<"DEV" | "PROD">("DEV");
  const [envName, setEnvName] = useState("OIDC_CLIENT_SECRET");
  const [storedAs, setStoredAs] = useState<string | null>(null);

  const store = useMutation({
    mutationFn: () =>
      keycloakApi
        .storeClientSecretInEnv(created.id, {
          serviceId: serviceId!,
          environment,
          envName: envName.trim() || undefined,
        })
        .then((r) => r.data),
    onSuccess: (d) => {
      setStoredAs(`${d.key} in ${d.environment}`);
      showSuccess(`Secret stored as ${d.key} (${d.environment})`);
    },
    onError: (e) => showError(e),
  });

  return (
    <Stack>
      <Alert color="yellow" title="Copy the client secret now">
        This is the only time it is shown. Close this dialog and it is gone from the screen.
      </Alert>
      <CopyField label="Client secret" value={created.secret} />
      <Divider label="Store in service env" labelPosition="center" />
      <Select
        label="Service"
        placeholder="Pick a service"
        searchable
        data={(services || []).map((s) => ({ value: s.id, label: s.name }))}
        value={serviceId}
        onChange={setServiceId}
      />
      <Group grow align="flex-start">
        <Select
          label="Environment"
          data={[
            { value: "DEV", label: "DEV" },
            { value: "PROD", label: "PROD" },
          ]}
          value={environment}
          onChange={(v) => setEnvironment((v as "DEV" | "PROD") || "DEV")}
        />
        <TextInput
          label="Variable name"
          value={envName}
          onChange={(e) => setEnvName(e.currentTarget.value)}
        />
      </Group>
      {storedAs && (
        <Alert color="teal" icon={<IconCheck size={16} />}>
          Stored as {storedAs}.
        </Alert>
      )}
      <Group position="right">
        <Button variant="light" disabled={!serviceId} loading={store.isLoading} onClick={() => store.mutate()}>
          Store in service env
        </Button>
        <Button onClick={onClose}>Done</Button>
      </Group>
    </Stack>
  );
}

function CreateClientModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [name, setName] = useState("");
  const [uris, setUris] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [restrict, setRestrict] = useState(true);
  const [protocol, setProtocol] = useState<"saml" | "oidc">("saml");
  const [preview, setPreview] = useState<string[] | null>(null);
  // Set after an OIDC create; the secret lives only in this state, so it is
  // shown once and gone when the dialog closes.
  const [created, setCreated] = useState<{ id: string; clientId: string; secret: string } | null>(null);

  const parseUris = () =>
    uris
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

  const dryRun = useMutation({
    mutationFn: () =>
      keycloakApi
        .provisionClient({
          clientId: clientId.trim(),
          redirectUris: parseUris(),
          baseUrl: baseUrl.trim(),
          name: name || undefined,
          restrictAccess: restrict,
          protocol,
          dryRun: true,
        })
        .then((r) => r.data),
    onSuccess: (d) => setPreview(d.steps),
    onError: (e) => showError(e),
  });

  const create = useMutation({
    mutationFn: () =>
      keycloakApi
        .provisionClient({
          clientId: clientId.trim(),
          redirectUris: parseUris(),
          baseUrl: baseUrl.trim(),
          name: name || undefined,
          restrictAccess: restrict,
          protocol,
        })
        .then((r) => r.data),
    onSuccess: (d) => {
      showSuccess(`Client "${d.clientId}" created`);
      qc.invalidateQueries({ queryKey: ["kc-clients"] });
      if (d.clientSecret && d.created) {
        setCreated({ id: d.created.id, clientId: d.clientId, secret: d.clientSecret });
      } else {
        onClose();
      }
    },
    onError: (e) => showError(e),
  });

  const valid = clientId.trim().length > 0 && parseUris().length > 0;

  if (created) {
    return (
      <Modal opened onClose={onClose} title={`OIDC client "${created.clientId}" created`} size="xl">
        <ClientSecretPanel created={created} onClose={onClose} />
      </Modal>
    );
  }

  return (
    <Modal opened onClose={onClose} title="Create client" size="xl">
      <Stack>
        <SegmentedControl
          aria-label="Protocol"
          value={protocol}
          onChange={(v) => {
            setProtocol(v as "saml" | "oidc");
            setPreview(null);
          }}
          data={[
            { label: "SAML", value: "saml" },
            { label: "OIDC", value: "oidc" },
          ]}
        />
        <TextInput
          label="Client ID"
          required
          placeholder="my-new-app"
          value={clientId}
          onChange={(e) => setClientId(e.currentTarget.value)}
        />
        <TextInput
          label="Display name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Textarea
          label="Redirect / callback URIs"
          description={
            protocol === "oidc"
              ? "One per line, e.g. https://my-app.com/auth/callback"
              : "One per line, e.g. https://api.my-app.com/api/auth/saml/callback"
          }
          minRows={3}
          value={uris}
          onChange={(e) => setUris(e.currentTarget.value)}
        />
        <TextInput
          label="Home URL"
          description="The app's sign-in page. The access-denied page links back here."
          placeholder="https://my-app.com/login"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.currentTarget.value)}
        />
        {protocol === "oidc" && (
          <Alert color="gray" title="OIDC client settings">
            <Text size="sm">
              Confidential client, standard flow only, PKCE S256. The client secret is shown once
              after you create it.
            </Text>
          </Alert>
        )}
        <Switch
          label="Restrict access (only <clientId>-access holders can log in)"
          checked={restrict}
          onChange={(e) => setRestrict(e.currentTarget.checked)}
        />
        {preview && (
          <Alert color="blue" title="Plan (dry run) — nothing created yet">
            <Stack spacing={4}>
              {preview.map((s, i) => (
                <Text size="sm" key={i}>
                  • {s}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
        <Group position="right">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="light" disabled={!valid} loading={dryRun.isLoading} onClick={() => dryRun.mutate()}>
            Preview
          </Button>
          <Button disabled={!valid} loading={create.isLoading} onClick={() => create.mutate()}>
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Per-client detail: redirect URIs + client roles.
function ClientDetailModal({ client, onClose }: { client: KcClient; onClose: () => void }) {
  const qc = useQueryClient();

  // --- redirect URIs + Home URL ---
  const [uris, setUris] = useState((client.redirectUris || []).join("\n"));
  const [baseUrl, setBaseUrl] = useState(client.baseUrl || "");
  const parseUris = () =>
    uris
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  const saveUris = useMutation({
    mutationFn: () =>
      keycloakApi.updateClient(client.id, { redirectUris: parseUris(), baseUrl: baseUrl.trim() }),
    onSuccess: () => {
      showSuccess("Client URLs updated");
      qc.invalidateQueries({ queryKey: ["kc-clients"] });
    },
    onError: (e) => showError(e),
  });

  // --- client roles ---
  const { data: roles } = useQuery({
    queryKey: ["kc-client-roles", client.id],
    queryFn: () => keycloakApi.listClientRoles(client.id).then((r) => r.data),
  });
  const invalidateRoles = () =>
    qc.invalidateQueries({ queryKey: ["kc-client-roles", client.id] });

  const [newRole, setNewRole] = useState("");
  const [deleteRole, setDeleteRole] = useState<KcRole | null>(null);

  const createRole = useMutation({
    mutationFn: () =>
      keycloakApi.createClientRole(client.id, {
        name: newRole.trim(),
        // New client roles are always composite over the access gate so holders
        // can log in.
        grantsAccess: true,
      }),
    onSuccess: () => {
      showSuccess("Role created");
      setNewRole("");
      invalidateRoles();
    },
    onError: (e) => showError(e),
  });

  const removeRole = useMutation({
    mutationFn: (roleName: string) => keycloakApi.deleteClientRole(client.id, roleName),
    onSuccess: () => {
      showSuccess("Role deleted");
      setDeleteRole(null);
      invalidateRoles();
    },
    onError: (e) => showError(e),
  });

  // --- role webhook ---
  const { data: webhook } = useQuery({
    queryKey: ["kc-client-webhook", client.id],
    queryFn: () => keycloakApi.getClientWebhook(client.id).then((r) => r.data),
  });
  const [whUrl, setWhUrl] = useState("");
  const [whSecret, setWhSecret] = useState("");
  useEffect(() => {
    if (webhook) setWhUrl(webhook.url || "");
  }, [webhook]);
  const saveWebhook = useMutation({
    mutationFn: () =>
      keycloakApi.setClientWebhook(client.id, {
        url: whUrl.trim(),
        ...(whSecret ? { secret: whSecret } : {}),
      }),
    onSuccess: () => {
      showSuccess("Webhook saved");
      setWhSecret("");
      qc.invalidateQueries({ queryKey: ["kc-client-webhook", client.id] });
    },
    onError: (e) => showError(e),
  });

  // --- SAML IdP config (realm-wide; what a downstream SP needs) ---
  const { data: saml, isLoading: samlLoading } = useQuery({
    queryKey: ["kc-saml-idp"],
    queryFn: () => keycloakApi.getSamlIdpConfig().then((r) => r.data),
  });

  const [tab, setTab] = useState<string | null>("uris");

  return (
    <Modal opened onClose={onClose} title={`Manage client — ${client.clientId}`} size="xl">
      <Tabs value={tab} onTabChange={setTab}>
        <Tabs.List mb="md" sx={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
          <Tabs.Tab value="uris">URLs</Tabs.Tab>
          <Tabs.Tab value="saml">SAML Setup</Tabs.Tab>
          <Tabs.Tab value="webhook">Webhook</Tabs.Tab>
          <Tabs.Tab value="users">Users</Tabs.Tab>
          <Tabs.Tab value="roles">Roles</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="uris">
          <Textarea
            label="Redirect / callback URIs"
            description="One per line"
            minRows={5}
            value={uris}
            onChange={(e) => setUris(e.currentTarget.value)}
          />
          <TextInput
            mt="md"
            label="Home URL"
            description="The app's sign-in page. The access-denied page links back here."
            placeholder="https://my-app.com/login"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.currentTarget.value)}
          />
          <Group position="right" mt="md">
            <Button
              disabled={parseUris().length === 0}
              loading={saveUris.isLoading}
              onClick={() => saveUris.mutate()}
            >
              Save
            </Button>
          </Group>
        </Tabs.Panel>

        <Tabs.Panel value="users">
          <ClientUsersPanel client={client} />
        </Tabs.Panel>

        <Tabs.Panel value="saml">
          <Text size="xs" color="dimmed" mb="sm">
            The four environment variables your service provider needs. Entry point
            and certificate are realm-wide; issuer is this client's ID and the
            callback URL is its redirect URI (from the previous tab).
          </Text>
          {samlLoading ? (
            <Center py="md">
              <Loader />
            </Center>
          ) : (
            <Stack spacing="sm">
              <CopyField
                label="SAML_ENTRY_POINT"
                description="IdP single sign-on URL"
                value={saml?.ssoServiceUrl || ""}
              />
              <CopyField
                label="SAML_ISSUER"
                description="This client's ID (the SP entity ID)"
                value={client.clientId}
              />
              <CopyField
                label="SAML_CALLBACK_URL"
                description="The SP's assertion consumer (callback) URL"
                value={parseUris()[0] || ""}
              />
              <div>
                <Group position="apart" mb={4}>
                  <Text size="sm" weight={500}>
                    SAML_CERT
                  </Text>
                  {saml?.signingCertificate && (
                    <CopyButton value={saml.signingCertificate} timeout={1500}>
                      {({ copied, copy }) => (
                        <Button
                          size="xs"
                          variant="light"
                          color={copied ? "teal" : undefined}
                          leftIcon={
                            copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                          }
                          onClick={copy}
                        >
                          {copied ? "Copied" : "Copy PEM"}
                        </Button>
                      )}
                    </CopyButton>
                  )}
                </Group>
                <Text size="xs" color="dimmed" mb={4}>
                  Realm X.509 signing certificate
                </Text>
                {saml?.signingCertificate ? (
                  <Textarea
                    readOnly
                    autosize
                    minRows={4}
                    maxRows={8}
                    value={saml.signingCertificate}
                    styles={{
                      input: { fontFamily: "monospace", fontSize: 12 },
                    }}
                  />
                ) : (
                  <Text size="sm" color="dimmed">
                    No active signing certificate found for this realm.
                  </Text>
                )}
              </div>
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="webhook">
          <Text size="xs" color="dimmed" mb="sm">
            Called (HMAC-signed) when a user's roles change, or they're
            disabled/deleted, on this client. The downstream app revokes the
            user's session on receipt.
          </Text>
          <Stack spacing="sm">
            <TextInput
              label="Webhook URL"
              placeholder="https://app.example.com/api/auth/session/revoke"
              value={whUrl}
              onChange={(e) => setWhUrl(e.currentTarget.value)}
            />
            <PasswordInput
              label="Signing secret"
              placeholder={
                webhook?.hasSecret
                  ? "\u2022\u2022\u2022\u2022\u2022\u2022 (set \u2014 leave blank to keep)"
                  : "Set a secret for HMAC signing"
              }
              value={whSecret}
              onChange={(e) => setWhSecret(e.currentTarget.value)}
            />
          </Stack>
          <Group position="right" mt="md">
            <Button loading={saveWebhook.isLoading} onClick={() => saveWebhook.mutate()}>
              Save webhook
            </Button>
          </Group>
        </Tabs.Panel>

        <Tabs.Panel value="roles">
          <ScrollArea>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Composite</th>
                <th style={{ width: 50 }} />
              </tr>
            </thead>
            <tbody>
              {(roles || []).map((r) => (
                <tr key={r.id}>
                  <td>
                    <Text size="sm" weight={500}>
                      {r.name}
                    </Text>
                  </td>
                  <td>{r.composite ? <Badge size="xs">composite</Badge> : "\u2014"}</td>
                  <td>
                    <ActionIcon color="red" onClick={() => setDeleteRole(r)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </td>
                </tr>
              ))}
              {(roles || []).length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <Text size="sm" color="dimmed" align="center" py="sm">
                      No client roles yet.
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          </ScrollArea>
          <Group align="flex-end" mt="sm">
            <TextInput
              placeholder="New role (e.g. support)"
              value={newRole}
              onChange={(e) => setNewRole(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              mb={2}
              disabled={!newRole.trim()}
              loading={createRole.isLoading}
              onClick={() => createRole.mutate()}
            >
              Add
            </Button>
          </Group>
        </Tabs.Panel>
      </Tabs>

      <ConfirmationModal
        opened={!!deleteRole}
        onClose={() => setDeleteRole(null)}
        onConfirm={() => deleteRole && removeRole.mutate(deleteRole.name)}
        title="Delete client role"
        message={`Delete role "${deleteRole?.name}" from ${client.clientId}? Users assigned this role will lose it.`}
        confirmLabel="Delete"
      />
    </Modal>
  );
}

function ClientsTab() {
  const [createOpen, setCreateOpen] = useState(false);
  const [detailClient, setDetailClient] = useState<KcClient | null>(null);

  const { data: clients, isLoading } = useQuery({
    queryKey: ["kc-clients"],
    queryFn: () => keycloakApi.listClients().then((r) => r.data),
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack>
      <Group position="right">
        <Button leftIcon={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
          New client (from console template)
        </Button>
      </Group>
      <Card shadow="sm" radius="md" withBorder>
      <ScrollArea>
      <Table highlightOnHover>
        <thead>
          <tr>
            <th>Client ID</th>
            <th>Protocol</th>
            <th>Status</th>
            <th>Redirect URIs</th>
          </tr>
        </thead>
        <tbody>
          {(clients || [])
            .slice()
            .sort((a, b) => a.clientId.localeCompare(b.clientId))
            .map((c) => (
              <tr
                key={c.id}
                style={{ cursor: "pointer" }}
                onClick={() => setDetailClient(c)}
              >
                <td>
                  <Text weight={500}>{c.clientId}</Text>
                </td>
                <td>
                  <Badge variant="light">{c.protocol || "—"}</Badge>
                </td>
                <td>
                  <Badge color={c.enabled ? "green" : "gray"}>
                    {c.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </td>
                <td>
                  <Text size="sm" color="dimmed">
                    {(c.redirectUris || []).length === 0
                      ? "—"
                      : `${c.redirectUris.length} URI${
                          c.redirectUris.length === 1 ? "" : "s"
                        }`}
                  </Text>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      </ScrollArea>
      </Card>

      {createOpen && <CreateClientModal onClose={() => setCreateOpen(false)} />}
      {detailClient && (
        <ClientDetailModal client={detailClient} onClose={() => setDetailClient(null)} />
      )}
    </Stack>
  );
}

// ===========================================================================
// Groups tab: founders + top-level groups with their role subgroups
// ===========================================================================

// Client roles mapped on a group: add per client, ✕ removes.
function GroupRolesPanel({ group }: { group: KcGroup }) {
  const qc = useQueryClient();
  const { data: mappings, isLoading } = useQuery({
    queryKey: ["kc-group-roles", group.id],
    queryFn: () => keycloakApi.getGroupClientRoles(group.id).then((r) => r.data),
  });
  const { data: clients } = useQuery({
    queryKey: ["kc-clients"],
    queryFn: () => keycloakApi.listClients().then((r) => r.data),
  });
  const [clientUuid, setClientUuid] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const { data: clientRoles } = useQuery({
    queryKey: ["kc-client-roles", clientUuid],
    queryFn: () => keycloakApi.listClientRoles(clientUuid!).then((r) => r.data),
    enabled: !!clientUuid,
  });
  // `restricted-access` is internal: the gate role grants it.
  const assignable = (clientRoles || []).filter((r) => r.name !== "restricted-access");

  const rows = useMemo(() => {
    const byClient = new Map<string, typeof mappings>();
    for (const m of mappings || []) byClient.set(m.clientUuid, [...(byClient.get(m.clientUuid) || []), m]);
    return Array.from(byClient, ([uuid, list]) => ({
      uuid,
      clientId: list![0].clientId,
      roles: groupRoles(list || []),
    })).sort((a, b) => a.clientId.localeCompare(b.clientId));
  }, [mappings]);

  const add = useMutation({
    mutationFn: () =>
      keycloakApi.addGroupClientRoles(
        group.id,
        clientUuid!,
        assignable.filter((r) => picked.includes(r.name))
      ),
    onSuccess: () => {
      showSuccess("Roles added");
      setPicked([]);
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  const remove = useMutation({
    mutationFn: async (m: { uuid: string; role: string }) => {
      const { data: roles } = await keycloakApi.listClientRoles(m.uuid);
      return keycloakApi.removeGroupClientRoles(
        group.id,
        m.uuid,
        roles.filter((r) => r.name === m.role)
      );
    },
    onSuccess: () => {
      showSuccess("Role removed");
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  return (
    <Stack>
      <Group align="flex-end" grow>
        <Select
          label="Client"
          placeholder="Pick a client"
          searchable
          data={(clients || [])
            .map((c) => ({ value: c.id, label: c.clientId }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          value={clientUuid}
          onChange={(v) => {
            setClientUuid(v);
            setPicked([]);
          }}
        />
        <MultiSelect
          label="Roles"
          placeholder="Pick roles"
          data={assignable.map((r) => ({ value: r.name, label: r.name }))}
          value={picked}
          onChange={setPicked}
          disabled={!clientUuid}
        />
      </Group>
      <Group position="right">
        <Button
          leftIcon={<IconPlus size={16} />}
          disabled={!clientUuid || !picked.length}
          loading={add.isLoading}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </Group>
      <Divider />
      {isLoading ? (
        <Loader size="sm" />
      ) : !rows.length ? (
        <Text size="sm" color="dimmed">
          This group has no client roles yet.
        </Text>
      ) : (
        <Table fontSize="sm" verticalSpacing="sm">
          <thead>
            <tr>
              <th>Client</th>
              <th>Roles</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.uuid}>
                <td style={{ wordBreak: "break-all" }}>{c.clientId}</td>
                <td>
                  <RoleChips
                    roles={c.roles}
                    onRemove={(role) => remove.mutate({ uuid: c.uuid, role })}
                    removing={
                      remove.isLoading && remove.variables?.uuid === c.uuid
                        ? remove.variables.role
                        : null
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Text size="xs" color="dimmed">
        Members of this group and of its subgroups hold these roles. Changes apply on their next
        login. The level roles include the <code>&lt;client&gt;-access</code> gate needed to sign
        in.
      </Text>
    </Stack>
  );
}

// Direct members of a group: add by email, ✕ removes.
function GroupMembersPanel({ group }: { group: KcGroup }) {
  const qc = useQueryClient();
  const { data: members, isLoading } = useQuery({
    queryKey: ["kc-group-members", group.id],
    queryFn: () => keycloakApi.getGroupMembers(group.id).then((r) => r.data),
  });
  const [userId, setUserId] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => keycloakApi.addUserToGroup(userId!, group.id),
    onSuccess: () => {
      showSuccess("Member added");
      setUserId(null);
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  const remove = useMutation({
    mutationFn: (id: string) => keycloakApi.removeUserFromGroup(id, group.id),
    onSuccess: () => {
      showSuccess("Member removed");
      invalidateRoleViews(qc);
    },
    onError: (e) => showError(e),
  });

  return (
    <Stack>
      <Group align="flex-end">
        <div style={{ flex: 1 }}>
          <UserSearchSelect value={userId} onChange={setUserId} />
        </div>
        <Button
          leftIcon={<IconPlus size={16} />}
          disabled={!userId}
          loading={add.isLoading}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </Group>
      <Divider />
      {isLoading ? (
        <Loader size="sm" />
      ) : !members?.length ? (
        <Text size="sm" color="dimmed">
          No members yet.
        </Text>
      ) : (
        <Table fontSize="sm" verticalSpacing="sm">
          <thead>
            <tr>
              <th>User</th>
              <th>Name</th>
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {members.map((u) => (
              <tr key={u.id}>
                <td>{u.email || u.username}</td>
                <td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</td>
                <td>
                  <ActionIcon
                    color="red"
                    loading={remove.isLoading && remove.variables === u.id}
                    onClick={() => remove.mutate(u.id)}
                    aria-label={`Remove ${u.email || u.username}`}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Text size="xs" color="dimmed">
        Members of subgroups are listed under those subgroups.
      </Text>
    </Stack>
  );
}

function GroupDetailModal({
  group,
  onClose,
  onDelete,
}: {
  group: KcGroup;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [tab, setTab] = useState<string | null>("roles");
  return (
    <Modal opened onClose={onClose} title={`Group — ${group.path}`} size="xl">
      <Tabs value={tab} onTabChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="roles" icon={<IconSettings size={16} />}>
            Roles
          </Tabs.Tab>
          <Tabs.Tab value="members" icon={<IconUsers size={16} />}>
            Members
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="roles">
          <GroupRolesPanel group={group} />
        </Tabs.Panel>
        <Tabs.Panel value="members">
          <GroupMembersPanel group={group} />
        </Tabs.Panel>
      </Tabs>
      <Group position="right" mt="lg">
        <Button color="red" variant="light" leftIcon={<IconTrash size={16} />} onClick={onDelete}>
          Delete group
        </Button>
      </Group>
    </Modal>
  );
}

function GroupsTab() {
  const qc = useQueryClient();
  const { data: tree, isLoading } = useQuery({
    queryKey: ["kc-group-tree"],
    queryFn: () => keycloakApi.groupTree().then((r) => r.data),
  });
  const { data: founders } = useQuery({
    queryKey: ["kc-founders"],
    queryFn: () => keycloakApi.founders().then((r) => r.data.group),
  });
  const [detail, setDetail] = useState<KcGroup | null>(null);
  const [toDelete, setToDelete] = useState<KcGroup | null>(null);
  const [newGroup, setNewGroup] = useState("");
  const [subgroupOf, setSubgroupOf] = useState<KcGroup | null>(null);
  const [subgroupName, setSubgroupName] = useState("");

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["kc-founders"] });
    invalidateRoleViews(qc);
  };

  const sync = useMutation({
    mutationFn: () => keycloakApi.syncFounders(),
    onSuccess: ({ data }) => {
      showSuccess(
        data.granted.length
          ? `Founders got roles on ${data.granted.map((g) => g.clientId).join(", ")}`
          : "Founders already hold every role"
      );
      refresh();
    },
    onError: (e) => showError(e),
  });

  const createTopGroup = useMutation({
    mutationFn: () => keycloakApi.createGroup(newGroup.trim()),
    onSuccess: () => {
      showSuccess("Group added");
      setNewGroup("");
      refresh();
    },
    onError: (e) => showError(e),
  });

  const createSubgroup = useMutation({
    mutationFn: () => keycloakApi.createSubgroup(subgroupOf!.id, subgroupName.trim()),
    onSuccess: () => {
      showSuccess("Subgroup added");
      setSubgroupOf(null);
      setSubgroupName("");
      refresh();
    },
    onError: (e) => showError(e),
  });

  const remove = useMutation({
    mutationFn: (g: KcGroup) => keycloakApi.deleteGroup(g.id),
    onSuccess: () => {
      showSuccess("Group deleted");
      setToDelete(null);
      setDetail(null);
      refresh();
    },
    onError: (e) => showError(e),
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const groups = (tree || [])
    .filter((g) => g.id !== founders?.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Stack>
      <Card shadow="sm" radius="md" withBorder>
        <Group position="apart">
          <div>
            <Text weight={500}>Founders</Text>
            <Text size="sm" color="dimmed">
              Every role on every console-managed client, including clients and roles added later.
            </Text>
          </div>
          <Group spacing="xs">
            {founders && (
              <Button variant="default" onClick={() => setDetail(founders)}>
                Manage
              </Button>
            )}
            <Button loading={sync.isLoading} onClick={() => sync.mutate()}>
              {founders ? "Sync roles" : "Create founders group"}
            </Button>
          </Group>
        </Group>
      </Card>

      <Group position="apart" align="flex-end">
        <Text weight={500}>Groups</Text>
        <Group spacing="xs">
          <TextInput
            placeholder="New group (e.g. acme)"
            value={newGroup}
            onChange={(e) => setNewGroup(e.currentTarget.value)}
          />
          <Button
            leftIcon={<IconPlus size={16} />}
            disabled={!newGroup.trim()}
            loading={createTopGroup.isLoading}
            onClick={() => createTopGroup.mutate()}
          >
            Add
          </Button>
        </Group>
      </Group>

      {!groups.length && (
        <Text size="sm" color="dimmed">
          No groups yet. Add one, then give it subgroups such as admins and developers.
        </Text>
      )}
      {groups.map((c) => (
        <Card key={c.id} shadow="sm" radius="md" withBorder>
          <Group position="apart">
            <Anchor component="button" weight={500} onClick={() => setDetail(c)}>
              {c.name}
            </Anchor>
            <Group spacing="xs">
              <Button
                size="xs"
                variant="default"
                leftIcon={<IconPlus size={14} />}
                onClick={() => setSubgroupOf(c)}
              >
                Subgroup
              </Button>
              <ActionIcon color="red" onClick={() => setToDelete(c)} aria-label={`Delete ${c.name}`}>
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Group>
          <Group spacing={6} mt="sm">
            {c.subGroups.map((s) => (
              <Button key={s.id} compact variant="light" onClick={() => setDetail(s)}>
                {s.name}
              </Button>
            ))}
            {!c.subGroups.length && (
              <Text size="sm" color="dimmed">
                No subgroups yet.
              </Text>
            )}
          </Group>
        </Card>
      ))}

      <Modal
        opened={!!subgroupOf}
        onClose={() => setSubgroupOf(null)}
        title={`New subgroup in ${subgroupOf?.name}`}
      >
        <Stack>
          <TextInput
            label="Name"
            placeholder="e.g. developers"
            value={subgroupName}
            onChange={(e) => setSubgroupName(e.currentTarget.value)}
          />
          <Group position="right">
            <Button variant="default" onClick={() => setSubgroupOf(null)}>
              Cancel
            </Button>
            <Button
              disabled={!subgroupName.trim()}
              loading={createSubgroup.isLoading}
              onClick={() => createSubgroup.mutate()}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {detail && (
        <GroupDetailModal
          group={detail}
          onClose={() => setDetail(null)}
          onDelete={() => setToDelete(detail)}
        />
      )}

      <ConfirmationModal
        opened={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && remove.mutate(toDelete)}
        title="Delete group"
        message={`Delete "${toDelete?.path}" and its subgroups? Their members lose the roles they got from it.`}
        confirmLabel="Delete"
      />
    </Stack>
  );
}

// ===========================================================================
// Page
// ===========================================================================

export function AdminKeycloakPage() {
  const [tab, setTab] = useState<string | null>("clients");

  const { data: status, isLoading } = useQuery({
    queryKey: ["kc-status"],
    queryFn: () => keycloakApi.status().then((r) => r.data),
    retry: false,
  });

  return (
    <PageTransition>
      <PageHeader
        title="Keycloak Management"
        description="Manage Keycloak clients (and their roles), users and groups. Superuser only."
      />

      {!isLoading && status && !status.configured && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          title="Keycloak not configured"
          mb="md"
        >
          The backend is missing Keycloak admin credentials. Set KEYCLOAK_BASE_URL,
          KEYCLOAK_REALM, KEYCLOAK_ADMIN_CLIENT_ID and KEYCLOAK_ADMIN_CLIENT_SECRET to
          enable this feature.
        </Alert>
      )}

      <Tabs value={tab} onTabChange={setTab}>
        <Tabs.List sx={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
          <Tabs.Tab value="clients" icon={<IconAppWindow size={16} />}>
            Clients
          </Tabs.Tab>
          <Tabs.Tab value="users" icon={<IconUsers size={16} />}>
            Users
          </Tabs.Tab>
          <Tabs.Tab value="groups" icon={<IconUsersGroup size={16} />}>
            Groups
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="clients" pt="md">
          <ClientsTab />
        </Tabs.Panel>
        <Tabs.Panel value="users" pt="md">
          <UsersTab />
        </Tabs.Panel>
        <Tabs.Panel value="groups" pt="md">
          <GroupsTab />
        </Tabs.Panel>
      </Tabs>
    </PageTransition>
  );
}
